import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Icon } from '@storable/canaveral-components';
import cx from 'classnames';
import useCurrentUser from '../../hooks/useCurrentUser';
import MediaGalleryRightPanel from '../MediaGalleryRightPanel';
import styles from './styles.scss';
import { INPUT_PERMITTED_IMAGES_TYPES } from '../../../modules/assets';
import { MANAGE } from '../../../modules/authorization/actions';
import { FACILITY_PAGES, NON_FACILITY_PAGES } from '../../../modules/authorization/subjects';
import MediaGalleryEdit from '../../containers/MediaGalleryEdit';
import MediaSelector from '../../containers/MediaGallerySelector';
import { EDIT, ALL, TRASH } from './constants';

const propTypes = {
  banners: PropTypes.shape({
    success: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
    info: PropTypes.string,
  }).isRequired,
  filter: PropTypes.string.isRequired,
  updateFilter: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  setBanners: PropTypes.func.isRequired,
  successfullyUploaded: PropTypes.number.isRequired,
  inProgress: PropTypes.number,
  uploadedMedium: PropTypes.shape(),
};

const defaultProps = {
  loading: undefined,
  errors: undefined,
  uploadedMedium: undefined,
  inProgress: 0,
};

const MediaGallery = ({
  banners,
  filter,
  updateFilter,
  onUpload,
  errors,
  loading,
  setBanners,
  successfullyUploaded,
  uploadedMedium,
  inProgress,
}) => {
  const [selectedMedium, setSelectedMedium] = useState();

  useEffect(() => {
    let success;
    let errs;
    let loadingText;

    if (successfullyUploaded > 0) {
      success = `Success!  ${successfullyUploaded} ${successfullyUploaded === 1 ? 'image has' : 'images have'} been uploaded`;
    }

    if (inProgress > 0) {
      const images = inProgress > 1 ? 'images' : 'image';
      loadingText = `Uploading ${inProgress} ${images}. To ensure a successful upload, please stay on this page until the upload is complete.`;
    }

    if (errors && errors.length > 0) {
      errs = errors;
    }

    setBanners({ success, errors: errs, loadingText });
  }, [successfullyUploaded, errors, inProgress]);

  useEffect(() => {
    if (uploadedMedium) {
      setSelectedMedium(uploadedMedium);
    }
  }, [uploadedMedium]);

  const uploadRef = useRef();
  const onClick = () => uploadRef.current.click();

  const currentUser = useCurrentUser();
  const unauthorized = currentUser.cannot(MANAGE, FACILITY_PAGES, NON_FACILITY_PAGES);

  const changeFilter = newFilter => () => {
    setBanners({});
    updateFilter(newFilter);
    setSelectedMedium(undefined);
  };

  if (filter === EDIT) {
    return (
      <MediaGalleryEdit
        selectedMedium={selectedMedium.mediumId}
        updateFilter={updateFilter}
        filter={filter}
        setSelectedMedium={setSelectedMedium}
        setBanners={setBanners}
        banners={banners}
      />
    );
  }

  return (
    <div className={styles.mediaGallery}>
      <div className={styles.manage}>
        <div className={styles.manageHeader}>
          <Button
            className={styles.upload}
            context="positive"
            icon="upload"
            onClick={onClick}
            disabled={loading || unauthorized}
          >
            Upload Media
          </Button>
          <input
            accept={INPUT_PERMITTED_IMAGES_TYPES}
            className={styles.uploadInput}
            disabled={unauthorized}
            ref={uploadRef}
            type="file"
            onChange={e => onUpload(e, uploadRef)}
            multiple
          />
          <p className={styles.manageLabel}>Manage</p>
        </div>
        <ul className={styles.mediaFileFilter}>
          <li>
            <Button
              onClick={changeFilter(ALL)}
              className={cx(styles.li, { [styles.active]: filter === ALL })}
              context="bare"
            >
              All Files
            </Button>
          </li>
          <li>
            <Button
              onClick={changeFilter(TRASH)}
              className={cx(styles.li, { [styles.active]: filter === TRASH })}
              context="bare"
            >
              Trash
            </Button>
          </li>
        </ul>
      </div>
      <div className={styles.center}>
        {(banners.success || banners.errors || banners.loadingText) && (
          <div className={styles.bannersBox}>
            {banners.loadingText && (
              <Alert
                className={styles.alert}
                context="info"
              >
                <Icon id="spinner" context="info" label="loading" className={styles.icon} />
                <span className={styles.alertText}>{banners.loadingText}</span>
              </Alert>
            )}
            {banners.success && (
              <Alert
                className={styles.alert}
                context="success"
              >
                <Icon id="success" context="success" label="success" className={styles.icon} />
                <span className={styles.alertText}>{banners.success}</span>
              </Alert>
            )}
            {banners.errors && (
              <Alert
                className={cx(styles.alert, styles.errorAlert)}
                context="danger"
              >
                {banners.errors.map(err => (
                  <div className={styles.error}>
                    <Icon id="warning" context="danger" label="warning" className={styles.icon} />
                    <div key={err} className={styles.alertText}>{err}</div>
                  </div>
                ))}
              </Alert>
            )}
          </div>
        )}
        <MediaSelector
          setSelectedMedium={setSelectedMedium}
          selectedMedium={selectedMedium}
          showTrash={filter === TRASH}
          showSearch={filter === ALL}
          searchClassName={styles.search}
          containerClassName={styles.selectorContainer}
        />
      </div>
      <div className={styles.actions}>
        {(selectedMedium || filter === TRASH) && (
          <MediaGalleryRightPanel
            selectedMedium={selectedMedium && selectedMedium.mediumId}
            setSelectedMedium={setSelectedMedium}
            filter={filter}
            setBanners={setBanners}
            updateFilter={updateFilter}
          />
        )}
      </div>
    </div>
  );
};

MediaGallery.propTypes = propTypes;
MediaGallery.defaultProps = defaultProps;

export default MediaGallery;
